/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import { Channel, Ducks, Minutes, Program, Seconds, Time, TimeUtils } from '@wit/vtv-x-foundation-lib';
import React, { Dispatch, RefObject, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { StoreState } from 'store/store';
import CLUBadgeEngine from 'core/services/core/badgeEngine/CLUBadgeEngine';
import TvGuideCard from './TvGuideCard';
import { TimeLimits } from './TVGuideGrid';
import ChannelLogo from '../../ChannelLogo';

interface Props {
  channel: Channel;
  timeLimits: TimeLimits;
  time: Time;
  now: Time;
  unitDuration: Minutes;
  unitSize: number;
  gridRef: RefObject<HTMLDivElement>;
  onProgramClick: (program: Program) => void;
  loaderMarginTop: number;
  loaderMarginBottom: number;
}

export const PROGRAM_MARGIN = 8;

function useFetchPast(channel: Channel, refresh: number, dispatch: Dispatch<any>, hasMorePast: boolean | undefined) {
  const fetchPast = useCallback(async () => {
    if (hasMorePast) {
      dispatch(Ducks.Epg.Window.Creators.loadMorePast(channel.epgId));
    }
  }, [channel.epgId, dispatch, hasMorePast]);

  useEffect(() => {
    if (refresh) {
      fetchPast();
    }
  }, [fetchPast, channel, refresh, hasMorePast]);
}

function useFetchFuture(
  channel: Channel,
  refresh: number,
  dispatch: Dispatch<any>,
  hasMoreFuture: boolean | undefined
) {
  const fetchFuture = useCallback(async () => {
    if (hasMoreFuture) {
      dispatch(Ducks.Epg.Window.Creators.loadMoreFuture(channel.epgId));
    }
  }, [channel.epgId, dispatch, hasMoreFuture]);
  useEffect(() => {
    if (refresh) {
      fetchFuture();
    }
  }, [fetchFuture, channel, hasMoreFuture, refresh]);
}

function TVGuideLine({
  channel,
  timeLimits,
  time,
  now,
  unitDuration,
  unitSize,
  gridRef,
  onProgramClick,
  loaderMarginTop,
  loaderMarginBottom,
}: Props): JSX.Element {
  const dispatch = useDispatch();
  const [pastRefresh, setPastRefresh] = useState(0);
  const [futureRefresh, setFutureRefresh] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  /**
   * Data selector
   */
  const programs = useSelector(
    (state: StoreState) => {
      if (channel.epgId) {
        const assets = Ducks.Epg.Window.Selectors.getAssets(state, channel.epgId);
        const assetsWithBadges = CLUBadgeEngine.getAssetsWithBadgesList(assets || []);
        // filter the assets to avoid rendering more than the time limits
        // since this creates a new array every time the selector runs an equalityFn is mandatory to avoid rerenders
        return assetsWithBadges?.filter(
          (asset) =>
            TimeUtils.isAfter(asset.endDate, timeLimits.start) && TimeUtils.isBefore(asset.startDate, timeLimits.end)
        );
      }
      return undefined;
    },
    // only rerender if the programs list changed in size
    (prev, next) => prev?.length === next?.length
  );

  /**
   * Past data logic
   */

  const hasMorePast = useSelector((state: StoreState) => {
    return Ducks.Epg.Window.Selectors.hasMorePast(state, channel.epgId);
  });

  useFetchPast(channel, pastRefresh, dispatch, hasMorePast);

  const handlePastObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      if (programs && entries.some((entry) => entry.isIntersecting)) {
        setPastRefresh((prev) => prev + 1);
      }
    },
    [programs]
  );

  const pastLoader = useCallback(
    (node) => {
      if (node) {
        const option = {
          root: gridRef.current,
          rootMargin: `${loaderMarginTop}px  0px ${loaderMarginBottom}px 0px`,
          threshold: 0,
        };
        const observer = new IntersectionObserver(handlePastObserver, option);
        observer.observe(node);
        return () => observer.unobserve(node);
      }
      return undefined;
    },
    [gridRef, handlePastObserver, loaderMarginBottom, loaderMarginTop]
  );

  /**
   * Future data logic
   */

  const hasMoreFuture = useSelector((state: StoreState) => {
    return Ducks.Epg.Window.Selectors.hasMoreFuture(state, channel.epgId);
  });

  useFetchFuture(channel, futureRefresh, dispatch, hasMoreFuture);

  const handleFutureObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      if (programs && entries.some((entry) => entry.isIntersecting)) {
        setFutureRefresh((prev) => prev + 1);
      }
    },
    [programs]
  );

  const futureLoader = useCallback(
    (node) => {
      if (node) {
        const option = {
          root: gridRef.current,
          rootMargin: `${loaderMarginTop}px  0px ${loaderMarginBottom}px 0px`,
          threshold: 0,
        };
        const observer = new IntersectionObserver(handleFutureObserver, option);
        observer.observe(node);
        return () => {
          observer.unobserve(node);
        };
      }
      return undefined;
    },
    [gridRef, handleFutureObserver, loaderMarginBottom, loaderMarginTop]
  );

  /**
   * Initial data loader
   */
  useEffect(() => {
    if (channel.epgId) {
      // TODO: calculate the current scroll position and set the time to the middle of the window
      dispatch(Ducks.Epg.Window.Creators.loadChannelEpg(channel.epgId, time));
    }
  }, [dispatch, channel, time]);

  /**
   * Past loader size
   */
  const [left, setLeft] = useState(0);

  /**
   * Set the past loader size
   */
  useLayoutEffect(() => {
    if (programs?.length) {
      let programStart: Seconds =
        TimeUtils.toUnixEpoch(programs[0].startDate) - TimeUtils.toUnixEpoch(timeLimits.start);
      programStart = programStart > 0 ? programStart : 0;
      let newLeft = (programStart * unitSize) / TimeUtils.minutesToSeconds(unitDuration);
      newLeft = newLeft - PROGRAM_MARGIN > 0 ? newLeft - PROGRAM_MARGIN : 0;
      setLeft(newLeft);
    }
  }, [programs, timeLimits.start, unitDuration, unitSize]);

  return (
    <div ref={ref} className="tv-guide-line" id={`TvGuideGrid_channel___${channel.id}`}>
      <div id={`TvGuideGrid_channelContainer___${channel.id}`} className="tv-guide-channel">
        <ChannelLogo bemBlock="TvGuideGrid" channel={channel} />
        <span id={`TvGuideGrid_channelNumber___${channel.id}`}>{channel.channelNumber}</span>
      </div>
      <ul>
        {left ? (
          <li
            ref={pastLoader}
            className="tv-guide-past-loader"
            style={{ width: left, visibility: hasMorePast ? 'visible' : 'hidden' }}
          />
        ) : null}
        {programs?.map((program) => {
          return (
            <TvGuideCard
              key={`${channel.epgId}-${program.startDate}`}
              program={program}
              onClick={onProgramClick}
              now={now}
              timeLimits={timeLimits}
              unitDuration={unitDuration}
              unitSize={unitSize}
              ctxId={channel.id}
            />
          );
        })}
        {hasMoreFuture &&
        programs?.length &&
        TimeUtils.isAfter(timeLimits.end, programs[programs.length - 1].endDate) ? (
          <li ref={futureLoader} className="tv-guide-future-loader" />
        ) : null}
        {programs === undefined ? <li className="tv-guide-future-loader" /> : null}
      </ul>
    </div>
  );
}

export default React.memo(TVGuideLine);
